import {
  ActionError,
  Button,
  ConfirmDialog,
  FormFooter,
  PageHeader,
  PageTitle,
  SavedIndicator,
  Skeleton,
  StatusTag,
  Surface,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  plural,
  titleCase,
  toast,
  useBreakpoint,
} from '@westhetics/ui'
import { useRef, useState, type FormEvent } from 'react'
import { Link, Navigate, useNavigate, useParams } from 'react-router'
import { LoadError } from '@/app/load-error'
import { Page } from '@/app/shell'
import {
  PatientAnamnesesTab,
  SEND_ANAMNESIS_ANCHOR,
} from '@/features/anamneses/patient-anamneses-tab'
import { useClinicProfile } from '@/features/clinic-profile/api'
import { placeFieldErrors } from '@/features/clinic-profile/form'
import { focusFirstInvalid, useFormState } from '@/features/clinic-profile/use-form-state'
import { isApiError } from '@/lib/api'
import { errorMessage } from '@/lib/errors'
import { useSession } from '@/lib/session'
import { routes } from '@/routes'
import {
  usePatient,
  usePatientAction,
  useUpdatePatient,
  type AwaitingSignature,
  type ExistingPatient,
  type Patient,
} from './api'
import {
  PATIENT_FORM_KEYS,
  changedBlocks,
  patientFieldKey,
  patientFormFrom,
  updatePatientInput,
  validatePatientForm,
  type PatientFormState,
} from './form'
import { clinicPendingCount, identificationLine, joinList, personWords } from './labels'
import { MobileActionBar } from './mobile-action'
import { PatientDetailsForm, PatientDetailsRead } from './patient-details-tab'
import { PatientDocumentsTab } from '@/features/documents/patient-documents-tab'
import { PatientTreatmentPlansTab } from '@/features/treatment-plans/patient-treatment-plans-tab'
import { PatientSummary } from './patient-summary'
import { GuardedLink, UnsavedDialog } from './unsaved-guard'
import { LeaveContext, useUnsavedGuard } from './use-unsaved-guard'

/**
 * A ficha do paciente (docs/areas/pacientes.md, "Ficha"; RN-PAC-09, 10, 17, 18 e 27). Arquétipo:
 * ficha com abas. A pergunta: "o que esta paciente tem, o que falta e o que eu faço agora?". O
 * topo traz o nome, a linha de identificação, o medidor e as pendências; cada aba trata de um
 * assunto só. A rota aceita `/patients/:id`, `/patients/:id/:tab`, a leitura de uma anamnese em
 * `/patients/:id/anamneses/:anamnesisId` (aba Anamnese) e a de um documento em
 * `/patients/:id/documents/:appointmentId/:documentId` (aba Documentos). Quem não é paciente
 * (aluno, funcionário, terceiro; RN-PAC-41) não tem anamnese nem plano: a ficha esconde as duas abas,
 * abre em Documentos e tem "Documentos avulsos" como ação principal.
 */

type PatientTab = 'details' | 'anamneses' | 'documents' | 'treatment-plans'

const TABS: PatientTab[] = ['details', 'anamneses', 'documents', 'treatment-plans']
const FORM_ID = 'patient-details-form'

export function PatientPage() {
  const { id, tab, anamnesisId } = useParams()
  const isDesk = useBreakpoint('desk')
  const query = usePatient(id)
  const patient = query.data

  // A ficha abre na Anamnese no computador (protótipo) e em Documentos no celular (a primeira
  // aba de lá); a leitura de uma anamnese pertence à aba Anamnese. Quem não é paciente não tem
  // Anamnese nem Planos (RN-PAC-41): abre em Documentos.
  const isPatient = (patient?.audience ?? 'patient') === 'patient'
  const tabs = isPatient ? TABS : TABS.filter((item) => item === 'details' || item === 'documents')
  const active: PatientTab = tabs.includes(tab as PatientTab)
    ? (tab as PatientTab)
    : anamnesisId && isPatient
      ? 'anamneses'
      : isDesk && isPatient
        ? 'anamneses'
        : 'documents'

  // Cadastro unificado: o id continua resolvendo para o principal (RN-PAC-17).
  if (patient?.status === 'merged' && patient.mergedIntoId) {
    const target =
      tab && TABS.includes(tab as PatientTab)
        ? routes.patientTab(patient.mergedIntoId, tab as PatientTab)
        : routes.patient(patient.mergedIntoId)
    return <Navigate to={target} replace />
  }

  if (!patient) {
    return (
      <>
        <PageHeader
          LinkComponent={Link}
          crumbs={[{ label: 'Pacientes', href: routes.patients }, { label: '…' }]}
          actions={
            <Button variant="outline" size="sm" asChild>
              <Link to={routes.patients}>Voltar</Link>
            </Button>
          }
        />
        <Page wide>
          {query.isError ? (
            <LoadError
              title="Não conseguimos abrir esta ficha"
              error={query.error}
              onRetry={() => void query.refetch()}
            />
          ) : (
            <div role="status" aria-label="Carregando" className="flex flex-col gap-4">
              <Skeleton className="h-[26px] w-[320px] max-w-full" />
              <Skeleton className="h-[13px] w-[460px] max-w-full" />
              <Skeleton className="mt-2 h-[72px] w-full" />
              <Skeleton className="mt-2 h-[13px] w-[380px] max-w-full" />
              <Skeleton className="h-[280px] w-full" />
            </div>
          )}
        </Page>
      </>
    )
  }

  // O formulário nasce do cadastro carregado; outro paciente, outro formulário.
  return <PatientRecord key={patient.id} patient={patient} active={active} isDesk={isDesk} />
}

function PatientRecord({
  patient,
  active,
  isDesk,
}: {
  patient: Patient
  active: PatientTab
  isDesk: boolean
}) {
  const session = useSession()
  const navigate = useNavigate()
  const canManage = session.canManageClinic
  // O público da pessoa (RN-PAC-36, 40 e 41): os rótulos e o que a ficha oferece.
  const isPatient = patient.audience === 'patient'
  const words = personWords(patient.audience)
  const clinic = useClinicProfile({ enabled: isDesk })
  const audiences = clinic.data?.audiences ?? []
  // O público trava no primeiro documento (RN-PAC-37); quem decide é a API.
  const isAudienceLocked = patient.counts.documents > 0 || patient.counts.anamneses > 0
  const isArchived = patient.status === 'archived'
  const isAnonymized = Boolean(patient.anonymizedAt)
  const isLocked = isArchived || patient.isReadOnly
  const canEdit = isDesk && !isLocked
  // "Paciente anonimizada" é frase do sistema, não nome de pessoa: sai como veio.
  const displayName = isAnonymized ? patient.name : titleCase(patient.name)

  // O que a tela abriu (ou salvou por último): é contra isso que o `PUT` diz o que mudou.
  const [baseline, setBaseline] = useState(() => patientFormFrom(patient))
  const form = useFormState<PatientFormState>(
    () => baseline,
    (values) => validatePatientForm(values, { whatsappOptional: baseline.whatsapp === '' }),
  )
  const formRef = useRef<HTMLFormElement>(null)
  const save = useUpdatePatient(patient.id)
  const action = usePatientAction()
  const [archiving, setArchiving] = useState(false)
  const [failure, setFailure] = useState<{ title: string; error: unknown } | null>(null)
  const guard = useUnsavedGuard(canEdit && form.dirty)

  function submit(event: FormEvent) {
    event.preventDefault()
    if (!canEdit) return
    if (!form.check(PATIENT_FORM_KEYS)) {
      focusFirstInvalid(formRef.current)
      return
    }
    const input = updatePatientInput(form.values, baseline)
    if (Object.keys(input).length === 0) {
      form.markSaved()
      toast('Nada mudou no cadastro.')
      return
    }
    save.mutate(input, {
      onSuccess: (saved) => {
        // O formulário passa a mostrar o que a API guardou (e-mail em minúsculas, nome aparado).
        const next = patientFormFrom(saved)
        setBaseline(next)
        form.update(next)
        form.markSaved()
        toast('Cadastro salvo.')
      },
      onError: (error) => {
        form.refuse(placeFieldErrors<PatientFormState>(error, patientFieldKey).fields)
        focusFirstInvalid(formRef.current)
      },
    })
  }

  function archive() {
    setFailure(null)
    action.mutate(
      { id: patient.id, action: 'archive' },
      {
        onSuccess: () => {
          setArchiving(false)
          toast(`${displayName} arquivada`, {
            // Desfazer por 10 segundos; a tela já saiu, então a resposta vem pela promessa.
            undo: () => {
              action
                .mutateAsync({ id: patient.id, action: 'restore' })
                .then(() => toast(`${displayName} restaurada.`))
                .catch((error: unknown) => toast(errorMessage(error)))
            },
          })
          navigate(routes.patients)
        },
        onError: (error) => {
          setArchiving(false)
          setFailure({ title: `Não foi possível arquivar "${displayName}"`, error })
        },
      },
    )
  }

  function restore() {
    setFailure(null)
    action.mutate(
      { id: patient.id, action: 'restore' },
      {
        onSuccess: () => toast(`${displayName} restaurada.`),
        onError: (error) =>
          setFailure({ title: `Não foi possível restaurar "${displayName}"`, error }),
      },
    )
  }

  const refused = save.isError
    ? placeFieldErrors<PatientFormState>(save.error, patientFieldKey)
    : null
  const showRefused =
    refused !== null && (Object.keys(refused.fields).length === 0 || refused.unplaced.length > 0)
  const conflict =
    save.error && isApiError(save.error) && save.error.code === 'conflict'
      ? (save.error.details.existing as ExistingPatient | undefined)
      : undefined
  const failureError = failure?.error
  const failureDetails = isApiError(failureError) ? failureError.details : {}
  const awaitingSignature = failureDetails.awaitingSignature as AwaitingSignature[] | undefined
  const failureExisting = failureDetails.existing as ExistingPatient | undefined

  const pendingCount = clinicPendingCount(patient)
  const { counts } = patient
  const kept = [
    counts.anamneses > 0 ? plural(counts.anamneses, 'anamnese') : null,
    counts.documents > 0 ? plural(counts.documents, 'documento') : null,
    counts.treatmentPlans.open + counts.treatmentPlans.closed > 0
      ? plural(counts.treatmentPlans.open + counts.treatmentPlans.closed, 'plano')
      : null,
  ].filter((item): item is string => item !== null)

  const restoreButton =
    canManage && isArchived && !isAnonymized && !patient.isReadOnly ? (
      <Button
        variant="outline"
        loading={action.isPending}
        loadingLabel="Restaurando…"
        onClick={restore}
      >
        Restaurar
      </Button>
    ) : null

  // O que o salvar recusou fica junto do formulário, até a pessoa corrigir.
  const notices = showRefused ? (
    <ActionError
      title="Não foi possível salvar o cadastro"
      description={
        refused.unplaced.length > 0
          ? refused.unplaced.join(' ')
          : `${errorMessage(save.error)} O que você digitou continua na tela.`
      }
      actions={
        conflict ? (
          <Button variant="outline" size="sm" asChild>
            <Link to={routes.patient(conflict.id)}>Abrir ficha</Link>
          </Button>
        ) : undefined
      }
    />
  ) : null

  // O envio da anamnese é da aba Anamnese: a ficha só leva até lá, já com o envio à vista.
  const sendAnamnesis =
    isPatient && patient.status === 'active' && !patient.isReadOnly ? (
      <Link
        to={{ pathname: routes.patientTab(patient.id, 'anamneses'), hash: SEND_ANAMNESIS_ANCHOR }}
      >
        Enviar anamnese
      </Link>
    ) : null
  // As ações de gerar documentos (RN-DOC-18, RN-DOC-07): a primária da ficha, fora da aba Dados.
  const canGenerate = patient.status === 'active' && !patient.isReadOnly
  const generateWithAnamnesis =
    canGenerate && isPatient ? (
      <Link to={routes.generateDocuments(patient.id)}>Gerar documento com anamnese</Link>
    ) : null
  const standaloneDocuments = canGenerate ? (
    <Link to={routes.standaloneDocuments(patient.id)}>Documentos avulsos</Link>
  ) : null

  // Arquivar fica num cartão à parte, separado do salvar; não há excluir (RT-19).
  const archiveCard =
    canManage && patient.status === 'active' && !patient.isReadOnly ? (
      <Surface className="flex flex-wrap items-center justify-between gap-4">
        <div className="max-w-[520px]">
          <div className="font-display text-lead text-ink leading-[1.3] font-normal">
            Arquivar {words.one}
          </div>
          <p className="font-body text-secondary mt-1 text-[12px] leading-[1.6] font-light">
            Sai das listas, da busca e das escolhas novas. Anamneses, documentos e planos continuam
            guardados, e o cadastro volta a qualquer momento.
          </p>
        </div>
        <Button variant="outline" onClick={() => setArchiving(true)}>
          Arquivar
        </Button>
      </Surface>
    ) : null

  return (
    <>
      <LeaveContext.Provider value={guard.leave}>
        <PageHeader
          LinkComponent={GuardedLink}
          crumbs={[{ label: words.titleMany, href: routes.patients }, { label: displayName }]}
          actions={
            <>
              <Button variant="outline" size="sm" onClick={() => guard.leave(routes.patients)}>
                Voltar
              </Button>
              {isDesk && standaloneDocuments ? (
                // Sem anamnese, os documentos avulsos são a ação principal (RN-PAC-41).
                <Button variant={isPatient ? 'outline' : 'primary'} size="sm" asChild>
                  {standaloneDocuments}
                </Button>
              ) : null}
              {isDesk && sendAnamnesis ? (
                <Button variant="outline" size="sm" asChild>
                  {sendAnamnesis}
                </Button>
              ) : null}
              {isDesk && generateWithAnamnesis ? (
                <Button size="sm" asChild>
                  {generateWithAnamnesis}
                </Button>
              ) : null}
            </>
          }
        />
      </LeaveContext.Provider>
      <Page wide>
        <PageTitle
          // No celular o rótulo sempre aparece; no computador, só quando a ficha não é de paciente
          // ("ALUNO", "TERCEIRO"): é o que distingue a ficha do mesmo desenho (RN-PAC-40).
          eyebrow={isDesk && isPatient ? undefined : words.title}
          title={displayName}
          meta={isDesk ? identificationLine(patient) : (patient.email ?? undefined)}
          subtitle={
            pendingCount > 0 ? (
              <span className="text-plum">{plural(pendingCount, 'pendência')}</span>
            ) : undefined
          }
          trailing={
            isArchived ? (
              <StatusTag level="waiting">{isAnonymized ? 'Anonimizado' : 'Arquivado'}</StatusTag>
            ) : undefined
          }
        />

        <PatientSummary
          patient={patient}
          canManage={canManage}
          isDesk={isDesk}
          activeTab={active}
          restore={restoreButton}
        />

        {failure ? (
          <ActionError
            className="mt-4"
            title={failure.title}
            description={`${errorMessage(failure.error)} Nada mudou no cadastro.`}
            onDismiss={() => setFailure(null)}
            actions={
              awaitingSignature ? (
                <Button variant="outline" size="sm" asChild>
                  <Link to={routes.signatures}>Ver assinaturas</Link>
                </Button>
              ) : failureExisting ? (
                <Button variant="outline" size="sm" asChild>
                  <Link to={routes.patient(failureExisting.id)}>Abrir a outra ficha</Link>
                </Button>
              ) : undefined
            }
          />
        ) : null}

        <Tabs
          className="mt-6"
          value={active}
          onValueChange={(value) => navigate(routes.patientTab(patient.id, value as PatientTab))}
        >
          {isDesk ? (
            <TabsList>
              <TabsTrigger value="details">Dados pessoais</TabsTrigger>
              {isPatient ? (
                <TabsTrigger value="anamneses">
                  Anamnese{counts.anamneses ? ` ${counts.anamneses}` : ''}
                </TabsTrigger>
              ) : null}
              <TabsTrigger value="documents">Documentos</TabsTrigger>
              {isPatient ? (
                <TabsTrigger value="treatment-plans">Planos de tratamento</TabsTrigger>
              ) : null}
            </TabsList>
          ) : (
            <TabsList>
              <TabsTrigger value="documents">Documentos</TabsTrigger>
              {isPatient ? (
                <TabsTrigger value="anamneses">
                  Anamnese{counts.anamneses ? ` ${counts.anamneses}` : ''}
                </TabsTrigger>
              ) : null}
              <TabsTrigger value="details">Dados</TabsTrigger>
              {isPatient ? <TabsTrigger value="treatment-plans">Planos</TabsTrigger> : null}
            </TabsList>
          )}
          <TabsContent value="details">
            {isDesk ? (
              <PatientDetailsForm
                formId={FORM_ID}
                formRef={formRef}
                form={form}
                audiences={audiences}
                isAudienceLocked={isAudienceLocked}
                readOnly={isLocked}
                sourceClinicName={patient.sourceClinic?.name}
                onSubmit={submit}
                notices={notices}
                actions={
                  canEdit ? (
                    <FormFooter
                      status={
                        <SavedIndicator
                          savedAt={new Date(patient.updatedAt)}
                          dirty={form.dirty}
                          saving={save.isPending}
                        />
                      }
                      secondary={
                        <Button variant="outline" onClick={() => guard.leave(routes.patients)}>
                          Cancelar
                        </Button>
                      }
                      primary={
                        <Button type="submit" loading={save.isPending} loadingLabel="Salvando…">
                          Salvar
                        </Button>
                      }
                    />
                  ) : null
                }
                footer={archiveCard}
              />
            ) : (
              <PatientDetailsRead patient={patient} notices={notices} footer={archiveCard} />
            )}
          </TabsContent>
          {isPatient ? (
            <TabsContent value="anamneses">
              <PatientAnamnesesTab patientId={patient.id} />
            </TabsContent>
          ) : null}
          <TabsContent value="documents">
            <PatientDocumentsTab patient={patient} />
          </TabsContent>
          {isPatient ? (
            <TabsContent value="treatment-plans">
              <PatientTreatmentPlansTab patient={patient} />
            </TabsContent>
          ) : null}
        </Tabs>
      </Page>

      {!isDesk && !sendAnamnesis && standaloneDocuments ? (
        <MobileActionBar>
          <Button size="lg" asChild>
            {standaloneDocuments}
          </Button>
        </MobileActionBar>
      ) : null}
      {!isDesk && sendAnamnesis ? (
        <MobileActionBar>
          <Button variant="outline" size="lg" asChild>
            {sendAnamnesis}
          </Button>
          {generateWithAnamnesis ? (
            <Button size="lg" asChild>
              <Link to={routes.generateDocuments(patient.id)}>Gerar documento</Link>
            </Button>
          ) : null}
        </MobileActionBar>
      ) : null}

      <ConfirmDialog
        open={archiving}
        onOpenChange={setArchiving}
        tone="primary"
        title={`Arquivar "${displayName}"?`}
        effect={`${isPatient ? 'A paciente' : 'O cadastro'} sai das listas e da busca. ${
          kept.length > 0 ? `Continuam guardados: ${joinList(kept)}.` : 'Nada é apagado.'
        } A ficha volta quando você restaurar.`}
        confirmLabel="Arquivar"
        loading={action.isPending}
        loadingLabel="Arquivando…"
        onConfirm={archive}
      />

      <UnsavedDialog
        open={guard.isAsking}
        blocks={changedBlocks(form.values, baseline)}
        kept="O que já foi salvo continua."
        onStay={guard.stay}
        onLeave={guard.confirm}
      />
    </>
  )
}
